import React, { useState, useEffect } from "react";
import { useLocation, NavLink, useNavigate } from "react-router-dom";
import { MainLogo } from "components/Images";
import { useSelector, useDispatch } from "react-redux";
import { logout, login as loginAction, setClinicBranding } from "client/slices/authSlice";
import { baseApi } from "BaseAPI";
import { updateTotalItem } from "client/slices/cartSlice";
import allRoutes from "client/constants/allRoutes";
import ProtectedFeature from "portal/components/ProtectedFeature";
import { useSubscription } from "context/SubscriptionProvider";

// --- Styling utilities ---

const navLinkClass = ({ isActive }) =>
  `flex items-center gap-2.5 px-3 py-[6px] rounded-lg [&>svg]:w-[18px] [&>svg]:h-[18px] [&>svg]:shrink-0 text-[13px] transition-all duration-150 ${
    isActive
      ? "bg-brand-500/10 text-brand-500 font-semibold"
      : "text-content-secondary hover:bg-surface-hover hover:text-content-primary font-normal"
  }`;

const subNavLinkClass = ({ isActive }) =>
  `flex items-center gap-2 pl-10 pr-3 py-[5px] rounded-lg text-[12.5px] transition-all duration-150 ${
    isActive
      ? "text-brand-500 font-semibold"
      : "text-content-secondary hover:text-content-primary hover:bg-surface-hover/50 font-normal"
  }`;

const collapsibleBtnClass = (isActive) =>
  `w-full flex items-center gap-2.5 px-3 py-[6px] rounded-lg [&>svg]:w-[18px] [&>svg]:h-[18px] [&>svg]:shrink-0 text-[13px] cursor-pointer transition-all duration-150 ${
    isActive
      ? "text-brand-500 font-semibold"
      : "text-content-secondary hover:bg-surface-hover hover:text-content-primary font-normal"
  }`;

// --- Sub-components ---

const SectionLabel = ({ children }) => (
  <li className="px-3 pt-3 pb-0.5 first:pt-1">
    <span className="text-[10px] font-semibold uppercase tracking-widest text-content-tertiary/50 select-none">
      {children}
    </span>
  </li>
);

const ChevronIcon = ({ isOpen }) => (
  <svg
    className={`!w-3.5 !h-3.5 ml-auto opacity-40 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

// --- Icons ---

const DashboardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 14v6m-3-3h6M6 10h2a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2zm10 0h2a2 2 0 002-2V6a2 2 0 00-2-2h-2a2 2 0 00-2 2v2a2 2 0 002 2zM6 20h2a2 2 0 002-2v-2a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2z" />
  </svg>
);

const AppointmentsIcon = () => (
  <svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.5 0C13.8549 9.6613e-08 14.1424 0.287666 14.1426 0.642578V1.92871H16.0713C16.5826 1.92871 17.0729 2.13175 17.4346 2.49316C17.7962 2.85484 18 3.34593 18 3.85742V16.0713C18 16.5828 17.7962 17.0729 17.4346 17.4346C17.0729 17.7962 16.5828 18 16.0713 18H1.92871C1.41722 18 0.927107 17.7962 0.56543 17.4346C0.203753 17.0729 0 16.5828 0 16.0713V3.85742C0 3.34593 0.203752 2.85484 0.56543 2.49316C0.927067 2.13175 1.41742 1.92871 1.92871 1.92871C2.28364 1.92879 2.57121 2.21636 2.57129 2.57129C2.57129 2.92628 2.28369 3.21379 1.92871 3.21387C1.75821 3.21387 1.59419 3.28178 1.47363 3.40234C1.35312 3.5229 1.28613 3.68696 1.28613 3.85742V16.0713C1.28613 16.2418 1.35307 16.4058 1.47363 16.5264C1.59419 16.6469 1.75822 16.7139 1.92871 16.7139H16.0713C16.2418 16.7139 16.4058 16.6469 16.5264 16.5264C16.6469 16.4058 16.7139 16.2418 16.7139 16.0713V3.85742C16.7139 3.68696 16.6469 3.5229 16.5264 3.40234C16.4058 3.28178 16.2418 3.21387 16.0713 3.21387H14.1426V4.5C14.1426 4.85504 13.855 5.14258 13.5 5.14258C13.145 5.14258 12.8574 4.85504 12.8574 4.5V0.642578C12.8576 0.287666 13.1451 0 13.5 0ZM10.0459 5.22266C10.3868 5.22273 10.714 5.35855 10.9551 5.59961C11.1961 5.84071 11.332 6.16788 11.332 6.50879V7.67383H12.4961C12.8367 7.67408 13.1644 7.80989 13.4053 8.05078C13.6457 8.29172 13.7811 8.61857 13.7812 8.95898V11.0488C13.7811 11.3895 13.6461 11.717 13.4053 11.958C13.1644 12.1989 12.8367 12.3337 12.4961 12.334H11.3311V13.498C11.3311 13.6667 11.2969 13.8344 11.2324 13.9902C11.1679 14.1461 11.0733 14.2889 10.9541 14.4082C10.8348 14.5275 10.6929 14.6229 10.5371 14.6875C10.3812 14.7521 10.2136 14.7841 10.0449 14.7842H7.95605C7.61528 14.7842 7.28795 14.649 7.04688 14.4082C6.80577 14.1671 6.66992 13.839 6.66992 13.498V12.334H5.50391C5.16314 12.334 4.8358 12.1988 4.59473 11.958C4.35372 11.717 4.2179 11.3896 4.21777 11.0488V8.95898C4.21788 8.61823 4.35384 8.29179 4.59473 8.05078C4.83584 7.80966 5.16291 7.67383 5.50391 7.67383H6.6709V6.50879C6.6709 6.16782 6.80677 5.84072 7.04785 5.59961C7.28896 5.35857 7.6161 5.22266 7.95703 5.22266H10.0459ZM7.95703 8.31641C7.95686 8.67105 7.66905 8.95859 7.31445 8.95898H5.50391V11.0488H7.3125C7.66737 11.049 7.95599 11.3365 7.95605 11.6914V13.498H10.0449V11.6914C10.045 11.3365 10.3326 11.0489 10.6875 11.0488H12.4961V8.95898H10.6895C10.3345 8.95898 10.0461 8.6713 10.0459 8.31641V6.50879H7.95703V8.31641ZM4.5 0C4.85495 3.22043e-08 5.14243 0.287666 5.14258 0.642578V1.92871H10.9287C11.2836 1.92879 11.5712 2.21636 11.5713 2.57129C11.5713 2.92628 11.2837 3.21379 10.9287 3.21387H5.14258V4.5C5.14258 4.85504 4.85504 5.14258 4.5 5.14258C4.14496 5.14258 3.85742 4.85504 3.85742 4.5V0.642578C3.85757 0.287666 4.14505 0 4.5 0Z" fill="currentColor" />
  </svg>
);

const WaitingListIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
    <rect x="9" y="3" width="6" height="4" rx="1" />
    <path d="M12 12v4" />
    <path d="M10 14h4" />
  </svg>
);

const PatientsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M6.28665 7.45231H6.37087H6.37089C6.37614 7.45231 6.37877 7.45231 6.38009 7.45362C6.3814 7.45494 6.3814 7.45757 6.3814 7.46283V7.46283C8.27644 7.39966 9.7609 5.86257 9.77143 3.957C9.77143 2.03038 8.19222 0.451172 6.2656 0.451172C4.33897 0.451172 2.75977 2.01985 2.75977 3.957C2.75977 5.85204 4.24421 7.38914 6.23401 7.45231H6.28665ZM4.33897 3.94648C4.33897 2.88315 5.20227 2.01985 6.2656 2.01985C7.33945 2.01985 8.20275 2.88315 8.20275 3.94648C8.19222 4.97822 7.40262 5.82047 6.3814 5.87311C6.28665 5.86258 6.20243 5.86258 6.1919 5.87311C5.14963 5.831 4.33897 4.98875 4.33897 3.94648ZM18.2866 7.45231H18.3709H18.3709C18.3761 7.45231 18.3788 7.45231 18.3801 7.45362C18.3814 7.45494 18.3814 7.45757 18.3814 7.46283C20.2764 7.39966 21.7609 5.86257 21.7714 3.957C21.7714 2.03038 20.1922 0.451172 18.2656 0.451172C16.339 0.451172 14.7598 2.01985 14.7598 3.957C14.7598 5.85204 16.2442 7.38914 18.234 7.45231H18.2866ZM16.339 3.94648C16.339 2.88315 17.2023 2.01985 18.2656 2.01985C19.3395 2.01985 20.2027 2.88315 20.2027 3.94648C20.1922 4.97822 19.4026 5.82047 18.3814 5.87311C18.2866 5.86258 18.2024 5.86258 18.1919 5.87311C17.1496 5.831 16.339 4.98875 16.339 3.94648ZM18.382 15.2112C17.9714 15.2112 17.5608 15.1796 17.1503 15.1059C16.7186 15.0322 16.4344 14.6216 16.508 14.19C16.5817 13.7583 16.9923 13.4741 17.424 13.5478C18.7189 13.7689 20.0876 13.5267 21.0035 12.9161C21.4983 12.5897 21.7615 12.1791 21.7615 11.7685C21.7615 11.3579 21.4878 10.9579 21.0035 10.6315C20.0876 10.0209 18.6979 9.77874 17.3924 10.0104C16.9608 10.0946 16.5502 9.79979 16.4765 9.36814C16.4028 8.9365 16.687 8.52591 17.1187 8.45221C18.8347 8.1469 20.614 8.47326 21.8773 9.3155C22.8038 9.93665 23.3407 10.821 23.3407 11.7685C23.3407 12.7055 22.8143 13.6004 21.8773 14.2321C20.9193 14.8638 19.677 15.2112 18.382 15.2112ZM2.19972 14.2321C3.15777 14.8638 4.40007 15.2112 5.69502 15.2112C6.10561 15.2112 6.51621 15.1796 6.9268 15.1059C7.35845 15.0322 7.6427 14.6216 7.56901 14.19C7.49531 13.7689 7.08472 13.4741 6.65307 13.5478C5.35812 13.7689 3.98948 13.5267 3.07355 12.9161C2.58926 12.5897 2.31553 12.1791 2.31553 11.7685C2.31553 11.3579 2.57873 10.9579 3.07355 10.6315C4.00001 10.0209 5.37918 9.77874 6.68466 10.0104C7.1163 10.0946 7.5269 9.79979 7.60059 9.36814C7.67429 8.9365 7.39003 8.52591 6.95838 8.45221C5.24232 8.1469 3.46308 8.47326 2.19972 9.3155C1.27326 9.93665 0.736328 10.8315 0.736328 11.7685C0.736328 12.716 1.27326 13.6109 2.19972 14.2321ZM12.0545 15.3175H11.9702H11.9176C9.92781 15.2544 8.44336 13.7173 8.44336 11.8222C8.44336 9.88508 10.0226 8.31641 11.9492 8.31641C13.8758 8.31641 15.455 9.89561 15.455 11.8222C15.4445 13.7278 13.96 15.2649 12.065 15.3281C12.065 15.3228 12.065 15.3202 12.0637 15.3189C12.0624 15.3175 12.0597 15.3175 12.0545 15.3175H12.0545ZM11.9492 9.88509C10.8859 9.88509 10.0226 10.7484 10.0226 11.8117C10.0226 12.854 10.8332 13.6962 11.8755 13.7383C11.886 13.7278 11.9702 13.7278 12.065 13.7383C13.0862 13.6857 13.8758 12.8435 13.8863 11.8117C13.8863 10.7589 13.023 9.88509 11.9492 9.88509ZM8.54738 22.0969C9.52648 22.7601 10.7898 23.0865 12.0532 23.0865C13.3166 23.0865 14.5799 22.7496 15.559 22.0969C16.496 21.4757 17.0224 20.5703 17.0224 19.6333C17.0224 18.6858 16.4855 17.8014 15.559 17.1803C13.6008 15.8748 10.5161 15.8748 8.54738 17.1803C7.61038 17.8014 7.08398 18.7069 7.08398 19.6438C7.08398 20.5914 7.62091 21.4757 8.54738 22.0969ZM8.66319 19.6544C8.66319 19.2438 8.92639 18.8332 9.4212 18.5068C10.853 17.5593 13.2639 17.5488 14.6747 18.5068C15.159 18.8332 15.4327 19.2333 15.4327 19.6438C15.4327 20.0544 15.1695 20.465 14.6747 20.7914C13.2534 21.7494 10.8425 21.7494 9.4212 20.7914C8.93692 20.465 8.66319 20.065 8.66319 19.6544Z" fill="currentColor" />
  </svg>
);

const StaffIcon = () => (
  <svg viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M1.81818 3.63636C2.30039 3.63636 2.76286 3.44481 3.10383 3.10383C3.44481 2.76286 3.63636 2.30039 3.63636 1.81818C3.63636 1.33597 3.44481 0.873508 3.10383 0.532533C2.76286 0.191558 2.30039 0 1.81818 0C1.33597 0 0.873508 0.191558 0.532533 0.532533C0.191558 0.873508 0 1.33597 0 1.81818C0 2.30039 0.191558 2.76286 0.532533 3.10383C0.873508 3.44481 1.33597 3.63636 1.81818 3.63636ZM6.54545 0.727273C6.25613 0.727273 5.97865 0.842207 5.77407 1.04679C5.56948 1.25138 5.45455 1.52885 5.45455 1.81818C5.45455 2.10751 5.56948 2.38499 5.77407 2.58957C5.97865 2.79416 6.25613 2.90909 6.54545 2.90909H18.9091C19.1984 2.90909 19.4759 2.79416 19.6805 2.58957C19.8851 2.38499 20 2.10751 20 1.81818C20 1.52885 19.8851 1.25138 19.6805 1.04679C19.4759 0.842207 19.1984 0.727273 18.9091 0.727273H6.54545ZM6.54545 13.0909C6.25613 13.0909 5.97865 13.2058 5.77407 13.4104C5.56948 13.615 5.45455 13.8925 5.45455 14.1818C5.45455 14.4711 5.56948 14.7486 5.77407 14.9532C5.97865 15.1578 6.25613 15.2727 6.54545 15.2727H18.9091C19.1984 15.2727 19.4759 15.1578 19.6805 14.9532C19.8851 14.7486 20 14.4711 20 14.1818C20 13.8925 19.8851 13.615 19.6805 13.4104C19.4759 13.2058 19.1984 13.0909 18.9091 13.0909H6.54545ZM5.45455 8C5.45455 7.71067 5.56948 7.4332 5.77407 7.22861C5.97865 7.02403 6.25613 6.90909 6.54545 6.90909H18.9091C19.1984 6.90909 19.4759 7.02403 19.6805 7.22861C19.8851 7.4332 20 7.71067 20 8C20 8.28933 19.8851 8.5668 19.6805 8.77139C19.4759 8.97597 19.1984 9.09091 18.9091 9.09091H6.54545C6.25613 9.09091 5.97865 8.97597 5.77407 8.77139C5.56948 8.5668 5.45455 8.28933 5.45455 8ZM3.63636 8C3.63636 8.48221 3.44481 8.94467 3.10383 9.28565C2.76286 9.62662 2.30039 9.81818 1.81818 9.81818C1.33597 9.81818 0.873508 9.62662 0.532533 9.28565C0.191558 8.94467 0 8.48221 0 8C0 7.51779 0.191558 7.05533 0.532533 6.71435C0.873508 6.37338 1.33597 6.18182 1.81818 6.18182C2.30039 6.18182 2.76286 6.37338 3.10383 6.71435C3.44481 7.05533 3.63636 7.51779 3.63636 8ZM1.81818 16C2.30039 16 2.76286 15.8084 3.10383 15.4675C3.44481 15.1265 3.63636 14.664 3.63636 14.1818C3.63636 13.6996 3.44481 13.2371 3.10383 12.8962C2.76286 12.5552 2.30039 12.3636 1.81818 12.3636C1.33597 12.3636 0.873508 12.5552 0.532533 12.8962C0.191558 13.2371 0 13.6996 0 14.1818C0 14.664 0.191558 15.1265 0.532533 15.4675C0.873508 15.8084 1.33597 16 1.81818 16Z" fill="currentColor" />
  </svg>
);

const BillingIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
  </svg>
);

const InsightsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SettingsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
  </svg>
);

const LogoutIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FullscreenEnterIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 9V5a1 1 0 011-1h4M20 9V5a1 1 0 00-1-1h-4M4 15v4a1 1 0 001 1h4M20 15v4a1 1 0 01-1 1h-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FullscreenExitIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 4v4a1 1 0 01-1 1H4M15 4v4a1 1 0 001 1h4M9 20v-4a1 1 0 00-1-1H4M15 20v-4a1 1 0 011-1h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SuperAdminIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-1 6h2v2h-2V7zm0 4h2v6h-2v-6z" fill="currentColor" />
  </svg>
);

// --- Main Component ---

const SideBar = ({ sidebarOpen, setSidebarOpen }) => {
  const user = useSelector((state) => state.auth.user);
  const permissions = useSelector((state) => state.auth.permissions) || [];
  const { isExpired, isTrialExpired, isGracePeriod, isAccountLocked, isNoSubscription } = useSubscription();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [clinicSwitcherOpen, setClinicSwitcherOpen] = useState(false);
  const [switchingClinic, setSwitchingClinic] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleFsChange);
    return () => document.removeEventListener("fullscreenchange", handleFsChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  };
  const ownedClinics = useSelector((state) => state.auth.ownedClinics) || [];
  const clinicBranding = useSelector((state) => state.auth.clinicBranding);
  const isAdminOrSuperAdmin = user?.RoleId === 1 || user?.RoleId === 2;

  // When the subscription is expired/locked — or the account has no
  // subscription yet (fresh paid signup that hasn't paid) — hide all menu
  // items except Subscription.
  const isSuperAdmin = user?.RoleId === 1;
  const isSubscriptionBlocked = !isSuperAdmin && (isExpired || isTrialExpired || isAccountLocked || isGracePeriod || isNoSubscription);

  // Permission checks — Admin/Super Admin bypass
  const hasBillingAccess = isAdminOrSuperAdmin || permissions.includes("view_finance") || permissions.includes("view_income_expense");
  // The Predictive / Therapy Intelligence PAGES: view_intelligence (2026-10-01; Admin and Super
  // Admin only before, owner's call 2026-09-24). Reception holds view_analytics for her
  // dashboard's At-Risk Patients, Advisor Tips and Smart Insights cards, but not this — no
  // built-in role does. The routes carry the same permission (Router.js), so this is not a
  // hidden link over an open page.
  const hasAnalyticsAccess = isAdminOrSuperAdmin || permissions.includes("view_intelligence");

  // Collapsible sections — accordion behavior: opening one closes the others
  const staffPaths = [allRoutes.employeelist, allRoutes.loginAccess, allRoutes.salaryManagement, allRoutes.deviceManager, allRoutes.leaveManagement];
  const isStaffActive = staffPaths.some((r) => location.pathname.startsWith(r));

  const billingPaths = [allRoutes.invoice, allRoutes.income, allRoutes.expense, allRoutes.billingSettlement];
  const isBillingActive = billingPaths.some((r) => location.pathname.startsWith(r));

  const settingsPaths = [allRoutes.services, allRoutes.sessionTypes, allRoutes.designations, allRoutes.rolelist, allRoutes.addrole, allRoutes.planCheckout, allRoutes.loginHistory, allRoutes.changePassword, allRoutes.clinicDetails, allRoutes.businessHours, allRoutes.appointmentSettings, allRoutes.taxSettings, allRoutes.invoiceSettings, allRoutes.integrationSettings, allRoutes.branding, allRoutes.helpSupport];
  const isSettingsActive = settingsPaths.some((r) => location.pathname.startsWith(r)) || location.pathname.includes("profile");

  const [openSection, setOpenSection] = useState(() => {
    if (isStaffActive) return "staff";
    if (isBillingActive) return "billing";
    if (isSettingsActive) return "settings";
    return null;
  });
  const staffOpen = openSection === "staff";
  const billingOpen = openSection === "billing";
  const settingsOpen = openSection === "settings";
  const toggleSection = (section) => setOpenSection((prev) => (prev === section ? null : section));

  // Auto-expand the active section when navigating to its child routes
  useEffect(() => {
    if (isStaffActive) setOpenSection("staff");
    else if (isBillingActive) setOpenSection("billing");
    else if (isSettingsActive) setOpenSection("settings");
  }, [location.pathname]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Insights active state (covers both predictive + therapy intelligence)
  const isInsightsActive =
    location.pathname.startsWith(allRoutes.predictiveIntelligence) ||
    location.pathname.startsWith(allRoutes.therapyIntelligence);

  const logoutHandle = () => setIsLogoutModalOpen(true);

  const handleSwitchClinic = async (clinic) => {
    if (clinic.id === user?.clinic_id) { setClinicSwitcherOpen(false); return; }
    setSwitchingClinic(clinic.id);
    try {
      const response = await baseApi.post("switch-clinic", { clinic_id: clinic.id });
      if (response?.rescode) {
        dispatch(loginAction({ ...response.data, ownedClinics }));
        if (response.data?.clinic) dispatch(setClinicBranding(response.data.clinic));
        localStorage.setItem("token", response.data.token);
        setClinicSwitcherOpen(false);
        window.location.href = "/portal";
      }
    } catch (e) {}
    setSwitchingClinic(null);
  };

  const confirmLogout = () => {
    dispatch(logout());
    dispatch(updateTotalItem());
    navigate(allRoutes.home);
    setIsLogoutModalOpen(false);
  };

  return (
    <div className="h-screen">
      {/* Mobile overlay */}
      <div
        className={`${
          sidebarOpen ? "block" : "hidden"
        } fixed inset-0 z-20 transition-opacity bg-black opacity-50 lg:hidden`}
        onClick={() => setSidebarOpen((prev) => !prev)}
      />

      <div
        className={`fixed lg:block inset-y-0 left-0 z-30 w-[220px] overflow-hidden transition duration-300 transform bg-surface-card lg:translate-x-0 lg:static lg:inset-0 customsidebar border-r border-surface-border ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo + wordmark */}
        <div className="flex items-center justify-center h-[68px] min-h-[68px] max-h-[68px] border-b border-surface-border bg-surface-card/70 backdrop-blur overflow-hidden">
          <div
            className="mainlogo cursor-pointer flex items-center gap-1.5"
            onClick={() => navigate(allRoutes.home)}
          >
            <img
              src={MainLogo}
              alt="Vistaar"
              className="object-contain"
              style={{ width: 44, height: 44 }}
            />
            <span
              className="font-extrabold uppercase tracking-wider leading-none"
              style={{ fontSize: 17, color: "#6366f1", letterSpacing: "0.04em" }}
            >
              Vistaar
            </span>
          </div>
        </div>

        {/* Navigation — on mobile leave room for bottom nav (60px) */}
        <nav className={`${user?.is_demo ? 'h-[calc(100dvh-68px-46px-60px)] lg:h-[calc(100vh-68px-46px)]' : 'h-[calc(100dvh-68px-60px)] lg:h-[calc(100vh-68px)]'} flex flex-col`}>
          <ul className="flex-1 overflow-y-auto px-2.5 pt-1 pb-2 space-y-px">

            {/* When subscription is blocked, only show Subscription link */}
            {isSubscriptionBlocked ? (
              <>
                <SectionLabel>Subscription</SectionLabel>
                <li>
                  <NavLink to={allRoutes.planCheckout} className={navLinkClass}>
                    <SettingsIcon />
                    <span>Subscription</span>
                  </NavLink>
                </li>
              </>
            ) : (
              <>
                {/* ═══════════ MAIN ═══════════ */}
                <SectionLabel>Main</SectionLabel>

                <li>
                  <NavLink to={allRoutes.portal} end className={navLinkClass}>
                    <DashboardIcon />
                    <span>Dashboard</span>
                  </NavLink>
                </li>

                <ProtectedFeature permission="view_appointments">
                  <li>
                    <NavLink to={allRoutes.appointment} className={navLinkClass}>
                      <AppointmentsIcon />
                      <span>Appointments</span>
                    </NavLink>
                  </li>
                </ProtectedFeature>

                <ProtectedFeature permission="view_appointments">
                  <li>
                    <NavLink to={allRoutes.waitingList} className={navLinkClass}>
                      <WaitingListIcon />
                      <span>Waiting List</span>
                    </NavLink>
                  </li>
                </ProtectedFeature>

                <ProtectedFeature permission="view_patients">
                  <li>
                    <NavLink to={allRoutes.allpatient} className={navLinkClass}>
                      <PatientsIcon />
                      <span>Patients</span>
                    </NavLink>
                  </li>
                </ProtectedFeature>

                <ProtectedFeature permission="manage_attendance">
                  <li>
                    <NavLink to={allRoutes.attendanceManagement} className={navLinkClass}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M9 16l2 2 4-4"/></svg>
                      <span>Attendance</span>
                    </NavLink>
                  </li>
                </ProtectedFeature>

                {(isAdminOrSuperAdmin || permissions.includes("view_employees") || permissions.includes("view_salary")) && (
                  <>
                    <li>
                      <button
                        onClick={() => toggleSection("staff")}
                        className={collapsibleBtnClass(isStaffActive)}
                      >
                        <StaffIcon />
                        <span>Staff</span>
                        <ChevronIcon isOpen={staffOpen} />
                      </button>
                    </li>

                    <div className={`overflow-hidden transition-all duration-200 ${staffOpen ? "max-h-52 opacity-100" : "max-h-0 opacity-0"}`}>
                      <ul className="space-y-px pt-px">
                        <ProtectedFeature permission="view_employees">
                          <li>
                            <NavLink to={allRoutes.employeelist} className={subNavLinkClass}>
                              <span className="w-1 h-1 rounded-full bg-current opacity-40 shrink-0" />
                              <span>Staff List</span>
                            </NavLink>
                          </li>
                        </ProtectedFeature>

                        {/* Credential management. Its actions (password, email, on/off, delete)
                            are manage_employees on the server, which no built-in role but Admin
                            holds; an Admin's own login stays Admin-only there. The page opens on
                            view_employees (Staff List's read-only directory). */}
                        {(isAdminOrSuperAdmin || permissions.includes("manage_employees")) && (
                          <li>
                            <NavLink to={allRoutes.loginAccess} className={subNavLinkClass}>
                              <span className="w-1 h-1 rounded-full bg-current opacity-40 shrink-0" />
                              <span>Login Access</span>
                            </NavLink>
                          </li>
                        )}

                        {/* Salary is its own permission — Accountant sees this
                            without view_employees (no full staff directory access) */}
                        <ProtectedFeature permission="view_salary">
                          <li>
                            <NavLink to={allRoutes.salaryManagement} className={subNavLinkClass}>
                              <span className="w-1 h-1 rounded-full bg-current opacity-40 shrink-0" />
                              <span>Salary</span>
                            </NavLink>
                          </li>
                        </ProtectedFeature>

                        {/* edit_employees as well as manage_employees: Reception runs
                            the front desk's device manager. Among roles 3-5 only she
                            holds edit_employees, so this pair means "Admin or the front
                            desk" — the backend's biometric write routes take the same
                            pair, so the page works rather than just appearing. */}
                        <ProtectedFeature permission={["manage_employees", "edit_employees"]}>
                          <li>
                            <NavLink to={allRoutes.deviceManager} className={subNavLinkClass}>
                              <span className="w-1 h-1 rounded-full bg-current opacity-40 shrink-0" />
                              <span>Biometric Devices</span>
                            </NavLink>
                          </li>
                        </ProtectedFeature>

                        {/* Leave list/browse uses view_employees, same as Staff
                            List above — approve/reject/cancel/allocate are
                            further gated by manage_employees inside the page itself. */}
                        <ProtectedFeature permission="view_employees">
                          <li>
                            <NavLink to={allRoutes.leaveManagement} className={subNavLinkClass}>
                              <span className="w-1 h-1 rounded-full bg-current opacity-40 shrink-0" />
                              <span>Leave</span>
                            </NavLink>
                          </li>
                        </ProtectedFeature>
                      </ul>
                    </div>
                  </>
                )}

                {/* ═══════════ FINANCE & BILLING ═══════════ */}
                {/* Each item keeps its own permission. Monthly Settlement moved from
                    manage_invoices to manage_billing on 2026-09-24: manage_invoices is
                    also what lets Reception take payments, so the two could not be
                    separated while they shared a slug. She keeps the section for
                    Invoices. Since 2026-10-01 she sees the settlement page again (who
                    was credited); its money buttons stay manage_billing's. */}
                {(hasBillingAccess || isAdminOrSuperAdmin || permissions.includes("manage_billing") || permissions.includes("manage_invoices")) && (
                  <>
                    <SectionLabel>Finance & Billing</SectionLabel>

                    <li>
                      <button
                        onClick={() => toggleSection("billing")}
                        className={collapsibleBtnClass(isBillingActive)}
                      >
                        <BillingIcon />
                        <span>Billing</span>
                        <ChevronIcon isOpen={billingOpen} />
                      </button>
                    </li>

                    <div className={`overflow-hidden transition-all duration-200 ${billingOpen ? "max-h-60 opacity-100" : "max-h-0 opacity-0"}`}>
                      <ul className="space-y-px pt-px">
                        {/* view_invoices as well as view_finance: Reception works the
                            invoice list, but its five headline totals and the CSV export
                            are view_finance and the backend leaves them out of her
                            response. */}
                        <ProtectedFeature permission={["view_finance", "view_invoices"]}>
                          <li>
                            <NavLink to={allRoutes.invoice} className={subNavLinkClass}>
                              <span className="w-1 h-1 rounded-full bg-current opacity-40 shrink-0" />
                              <span>Invoices</span>
                            </NavLink>
                          </li>
                        </ProtectedFeature>

                        <ProtectedFeature permission="view_income_expense">
                          <li>
                            <NavLink to={allRoutes.income} className={subNavLinkClass}>
                              <span className="w-1 h-1 rounded-full bg-current opacity-40 shrink-0" />
                              <span>Income</span>
                            </NavLink>
                          </li>
                        </ProtectedFeature>

                        <ProtectedFeature permission="view_income_expense">
                          <li>
                            <NavLink to={allRoutes.expense} className={subNavLinkClass}>
                              <span className="w-1 h-1 rounded-full bg-current opacity-40 shrink-0" />
                              <span>Expenses</span>
                            </NavLink>
                          </li>
                        </ProtectedFeature>

                        {/* Reception sees it too; crediting stays manage_billing (MonthlySettlement.jsx) */}
                        <ProtectedFeature permission={["manage_billing", "manage_invoices"]}>
                          <li>
                            <NavLink to={allRoutes.billingSettlement} className={subNavLinkClass}>
                              <span className="w-1 h-1 rounded-full bg-current opacity-40 shrink-0" />
                              <span>Monthly Settlement</span>
                            </NavLink>
                          </li>
                        </ProtectedFeature>
                      </ul>
                    </div>
                  </>
                )}

                {/* ═══════════ ANALYTICS ═══════════ */}
                {hasAnalyticsAccess && (
                  <>
                    <SectionLabel>Analytics</SectionLabel>

                    <li>
                      <NavLink
                        to={allRoutes.predictiveIntelligence}
                        className={() => navLinkClass({ isActive: isInsightsActive })}
                      >
                        <InsightsIcon />
                        <span>Predictive Intelligence</span>
                      </NavLink>
                    </li>
                  </>
                )}

                {/* ═══════════ SETTINGS ═══════════ */}
                <SectionLabel>Settings</SectionLabel>

                <li>
                  <button
                    onClick={() => toggleSection("settings")}
                    className={collapsibleBtnClass(isSettingsActive)}
                  >
                    <SettingsIcon />
                    <span>Settings</span>
                    <ChevronIcon isOpen={settingsOpen} />
                  </button>
                </li>

                <div className={`overflow-hidden transition-all duration-200 ${settingsOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
                  <ul className="space-y-px pt-px">
                    <li>
                      <NavLink
                        to={allRoutes.profile}
                        className={subNavLinkClass}
                      >
                        <span className="w-1 h-1 rounded-full bg-current opacity-40 shrink-0" />
                        <span>All Settings</span>
                      </NavLink>
                    </li>

                    {isAdminOrSuperAdmin && (
                      <li>
                        <NavLink to={allRoutes.myClinics} className={subNavLinkClass}>
                          <span className="w-1 h-1 rounded-full bg-current opacity-40 shrink-0" />
                          <span>My Clinics</span>
                        </NavLink>
                      </li>
                    )}

                    {(isAdminOrSuperAdmin || permissions.includes("view_services")) && (
                      <li>
                        <NavLink to={allRoutes.services} className={subNavLinkClass}>
                          <span className="w-1 h-1 rounded-full bg-current opacity-40 shrink-0" />
                          <span>Services</span>
                        </NavLink>
                      </li>
                    )}

                    {(isAdminOrSuperAdmin || permissions.includes("view_session_types")) && (
                      <li>
                        <NavLink to={allRoutes.sessionTypes} className={subNavLinkClass}>
                          <span className="w-1 h-1 rounded-full bg-current opacity-40 shrink-0" />
                          <span>Session Types</span>
                        </NavLink>
                      </li>
                    )}

                    {/* The page opens on view_designations (Router.js), which no built-in role but
                        Admin holds by default; a role an Admin gives it to sees the link too. */}
                    {(isAdminOrSuperAdmin || permissions.includes("view_designations")) && (
                      <li>
                        <NavLink to={allRoutes.designations} className={subNavLinkClass}>
                          <span className="w-1 h-1 rounded-full bg-current opacity-40 shrink-0" />
                          <span>Designations</span>
                        </NavLink>
                      </li>
                    )}

                    {/* What each role may do — Admin only, like the page itself (adminOnly in Router.js). */}
                    {isAdminOrSuperAdmin && (
                      <li>
                        <NavLink to={allRoutes.rolelist} className={subNavLinkClass}>
                          <span className="w-1 h-1 rounded-full bg-current opacity-40 shrink-0" />
                          <span>Roles & Permissions</span>
                        </NavLink>
                      </li>
                    )}

                    {isAdminOrSuperAdmin && (
                      <li>
                        <NavLink to={allRoutes.planCheckout} className={subNavLinkClass}>
                          <span className="w-1 h-1 rounded-full bg-current opacity-40 shrink-0" />
                          <span>Subscription</span>
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </div>

                <li>
                  <button
                    onClick={toggleFullscreen}
                    className={`w-full flex items-center gap-2.5 px-3 py-[6px] rounded-lg [&>svg]:w-[18px] [&>svg]:h-[18px] [&>svg]:shrink-0 text-[13px] text-content-secondary hover:bg-brand-500/10 hover:text-brand-500 transition-all duration-150 font-normal`}
                  >
                    {isFullscreen ? <FullscreenExitIcon /> : <FullscreenEnterIcon />}
                    <span>{isFullscreen ? "Exit Fullscreen" : "Fullscreen"}</span>
                  </button>
                </li>
              </>
            )}

            {/* Super Admin Panel — RoleId 1 only */}
            {user?.RoleId === 1 && (
              <>
                <li className="pt-3 pb-1">
                  <div className="border-t border-surface-border" />
                </li>
                <li>
                  <NavLink
                    to={allRoutes.superadmin}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-[6px] rounded-lg [&>svg]:w-[18px] [&>svg]:h-[18px] [&>svg]:shrink-0 text-[13px] transition-all duration-150 ${
                        isActive
                          ? "bg-brand-500/10 text-brand-500 font-semibold"
                          : "text-content-secondary hover:bg-surface-hover hover:text-content-primary font-normal"
                      }`
                    }
                  >
                    <SuperAdminIcon />
                    <span>Super Admin Panel</span>
                    <svg className="!w-3 !h-3 ml-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </NavLink>
                </li>
              </>
            )}
          </ul>

          {/* ═══════════ FIXED BOTTOM ═══════════ */}
          <div className="px-2.5 py-2 border-t border-surface-border">
            <button
              onClick={logoutHandle}
              className={`w-full flex items-center gap-2.5 px-3 py-[6px] rounded-lg [&>svg]:w-[18px] [&>svg]:h-[18px] [&>svg]:shrink-0 text-[13px] text-content-secondary hover:bg-red-50 hover:text-red-500 transition-all duration-150 font-normal`}
            >
              <LogoutIcon />
              <span>Logout</span>
            </button>
            <p className="text-center pt-2 pb-1 text-[10px] text-gray-400">
              Powered by <span className="text-brand-500 font-medium">Vistaar</span>
            </p>
          </div>
        </nav>
      </div>

      {/* Logout Confirmation Modal */}
      {isLogoutModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative bg-surface-card w-[420px] max-w-[90%] rounded-modal shadow-elevation-3 p-6 animate-fadeIn">
            <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-danger-100">
              <svg className="w-7 h-7 text-danger-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M4.93 19h14.14c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.2 16c-.77 1.33.19 3 1.73 3z" />
              </svg>
            </div>
            <h3 className="text-title text-content-primary text-center">Confirm Logout</h3>
            <p className="text-body text-content-secondary text-center mt-2 mb-6">
              Are you sure you want to logout? You will need to login again to access your dashboard.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setIsLogoutModalOpen(false)}
                className="px-5 py-2.5 text-body font-medium text-content-primary bg-surface-hover rounded-button hover:bg-gray-200 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="px-5 py-2.5 text-body font-semibold text-white bg-danger-500 rounded-button shadow-elevation-1 hover:bg-danger-600 transition-all"
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SideBar;
